import { useEffect } from 'react';

import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';
import {
	type TeamsPermissions,
	type TeamsPermissionsService,
	useTeamsPermissionsService,
} from '@atlassian/teams-common/use-teams-permissions';

import { logException } from '../../../../common/utils/sentry';
import { useV3OrgIdStatus } from '../../../org/hooks';
import { useSite } from '../../../site/hooks';

export type { TeamsPermissions };

/**
 * @deprecated This hook is deprecated and always returns true.
 */
export const useTeamsVisibilityEnabled = () => {
	return {
		isTeamsVisiblityEnabled: true,
		failOpenSites: false,
	};
};

const useTeamPermissionsAnalytics = ({
	data,
	error,
	permissions,
}: ReturnType<TeamsPermissionsService>) => {
	const { fireEvent } = usePeopleTeamAnalyticsEvents();

	useEffect(() => {
		if (error) {
			logException(error, 'Error loading team permissions in useTeamsPermissionsService');
			fireEvent('operational.teamsPermissionsRequest.failed', {
				message: error.message,
				status: error.status,
				path: error.path,
			});
		}
	}, [error, fireEvent]);

	useEffect(() => {
		if (data && permissions) {
			fireEvent('operational.teamsPermissionsRequest.succeeded', {
				canCreateTeams: permissions.canCreateTeams,
				canViewTeams: permissions.canViewTeams,
				canAdminTeams: !!permissions.canAdminTeams,
			});
		}
	}, [data, fireEvent, permissions]);
};

export const useTeamVisibility = (): {
	isLoading: boolean;
	permissions: TeamsPermissions;
} => {
	const { orgId } = useV3OrgIdStatus();
	const [{ cloudId }] = useSite();

	const result = useTeamsPermissionsService(
		{ orgId, cloudId },
		{
			enabled: true,
		},
	);

	useTeamPermissionsAnalytics(result);

	return {
		isLoading: result.isLoading,
		permissions: result.permissions,
	};
};
